import { z } from "zod";
import { PaginationRequestSchema } from "./pagination";
import { TournamentFormatConfigSchema } from "./stage";
import { SeasonWithStatusSchema } from "./season";

export const TournamentQuerySchema = PaginationRequestSchema.extend({
  name: z.string().optional(),
  type: z.string().optional(),
  disciplineId: z.string().optional(),
  countryId: z.string().optional(),
  statusId: z.string().optional(),
  seasonId: z.string().optional(),
  formatId: z.string().optional(),
  seasonStartFrom: z.string().optional(),
  seasonStartTo: z.string().optional(),
});
export type TournamentQueryDTO = z.infer<typeof TournamentQuerySchema>;

// Query schema para tipos de torneo
// Tournament Features Schema
export const TournamentFeaturesSchema = z.object({
  hasReferees: z.boolean(),
  hasLineups: z.boolean(),
  hasStadium: z.boolean(),
  hasTeams: z.boolean(),
  hasSubstitutions: z.boolean(),
  hasCards: z.boolean(),
  maxPlayersPerTeam: z.number().optional(),
  minPlayersPerTeam: z.number().optional(),
  eventTypes: z.array(z.string()),
});

// Tournament Response Schema
export const TournamentResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.string(),
  configuration: TournamentFeaturesSchema.optional(),
  logo: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
  discipline: z
    .object({
      id: z.string(),
      name: z.string(),
      sortOrder: z.number(),
      isSystem: z.boolean(),
      canDelete: z.boolean(),
      canEdit: z.boolean(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      deletedAt: z.coerce.date().nullable().optional(),
    })
    .optional(),
  country: z
    .object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      flag: z.string().nullable().optional(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
      deletedAt: z.coerce.date().nullable().optional(),
    })
    .optional(),
  statusIds: z.array(z.string()).optional(),
  subCategoryIds: z.array(z.string()).optional(),
  autoCategories: z.array(z.string()).optional(),
  seasons: z.array(SeasonWithStatusSchema).optional(),
  isSystem: z.boolean(),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type TournamentResponseDTO = z.infer<typeof TournamentResponseSchema>;

// Paginated Tournaments Response
import { PaginationResponseSchema } from "./pagination";

export const TournamentsPaginatedResponseSchema = PaginationResponseSchema(
  TournamentResponseSchema,
);
export type TournamentsPaginatedResponseDTO = z.infer<
  typeof TournamentsPaginatedResponseSchema
>;

// Tournament create / update requests
const SeasonCreateSchema = SeasonWithStatusSchema.extend({
  id: z.string().optional(),
  formatId: z.string().min(1, "La referencia de formato es requerida"),
});

export const CreateTournamentRequestSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  description: z.string().optional(),
  type: z.string().min(1, "El tipo es requerido"),
  disciplineId: z.string().min(1, "La referencia de disciplina es requerida"),
  countryId: z.string().optional(),
  configuration: TournamentFeaturesSchema.optional(),
  logo: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
  statusIds: z.array(z.string()).optional(),
  statusId: z.string().optional(),
  subCategoryIds: z.array(z.string()).optional(),
  autoCategories: z.array(z.string()).optional(),
  seasons: z.array(SeasonCreateSchema).optional(),
  isSystem: z.boolean().optional().default(false),
  canEdit: z.boolean().optional().default(true),
  canDelete: z.boolean().optional().default(true),
});

export const UpdateTournamentRequestSchema =
  CreateTournamentRequestSchema.partial();

export type CreateTournamentRequestDTO = z.infer<
  typeof CreateTournamentRequestSchema
>;
export type UpdateTournamentRequestDTO = z.infer<
  typeof UpdateTournamentRequestSchema
>;
